import { gql } from "@apollo/client";
import { faCheckSquare, faPen, faRefresh, faTrash, faUpRightFromSquare, faXmarkSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AppLink from "components/common/AppLink";
import ComponentState, { ComponentStateTypes } from "components/common/ComponentState";
import HTMLCode from "components/common/HTMLCode";
import If from "components/common/If";
import Button from "components/common/button/Button";
import ItemView from "components/common/views/ItemView";
import { ItemViewPropsType } from "components/common/views/ItemView";
import { api, graph } from "config/axios-context";
import { AppRoutes } from "config/router";
import { useEffect, useState } from "react";
import { EventViewDataQuery, EventViewDataQueryVariables, InsertTypes } from "types/graphql";
import { Roles } from "utilities/app-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import RoomTypeView from "../roomType/RoomTypeView";
import { SeasonType } from "types/season-type";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import { useSearchParams } from "react-router-dom";
import ModalPortal from "components/modal/ModalPortal";
import EventForm from "./EventForm";
import BookingIdView from "../booking/views/BookingIdView";
import { changeEventIsDone } from "utilities/entities/accommodation/event-utilities";
import BooleanView from "components/common/views/BooleanView";
import EventNameView from "./partial/EventNameView";
import { useTranslation } from "react-i18next";
import Translate from "components/common/Translate";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";

const DELETE_MODAL_HASH = "event-view-delete-modal";
const ADD_MODAL_ID = 'add-event-modal-from-view'

const DELETE_GROUP_EVENT_MODE = [
    {
        id: "current",
        name: "accommodation.event.delete.current"
    },
    {
        id: "future",
        name: "accommodation.event.delete.future"
    },
    {
        id: "all",
        name: "accommodation.event.delete.all"
    }
]

export type EventViewModel = {
    booking?: {
        id: number,
        inSeasonId: number,
        inDate: Date,
        outDate: Date,
        roomType: {
            id: number,
            name: string,
            color?: string | undefined | null,
        }
        season: SeasonType
    } | null
    order: number | string,
    id?: number,
    name: string,
    note?: string,
    date?: Date | null,
    group?: string | null,
    isDone: boolean,
    bookingId?: number | null,
    insertType: InsertTypes,
    assigneeId?: number | null,
    assignee?: {
        id: number,
        name: string
    } | null
}

type EventViewPropsType = {
    model: number | string | EventViewModel,
    name: string,
    onUpdate?: () => void,
    updateUrl: string
}

const query = gql`
query EventViewData($id: Int) {
    bookingEvent(id: $id) {
      bookingId
      isDone
      date
      note
      name
      insertType
      id,
      group
      order,
      assigneeId,
      assignee {
        id
        name
      },
      booking {
        id
        inDate
        inSeasonId
        outDate
        season {
            id
            name
            active
        }
        roomType {
            id
            name
            color
        }
      }
    }
  }
`;

const EventView = (props: EventViewPropsType) => {
    const context = useAppContext();
    const [t] = useTranslation();
    const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');
    const [model, setModel] = useState<EventViewModel | null>(typeof props.model == 'object' ? props.model : null);
    const [searchParams] = useSearchParams();
    const hasWriteRole = context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_WRITE);

    const [deleteConfig, setDeleteConfig] = useState(DELETE_GROUP_EVENT_MODE[0]);

    // #region load data
    useEffect(() => {
        if (!model) {
            loadData().then();
        } else {
            setComponentState('');
        }
    }, []);

    const loadData = async () => {
        setComponentState('loading');
        try {
            const response = await graph<EventViewDataQuery, EventViewDataQueryVariables>({
                query,
                variables: {
                    id: Number(typeof props.model == 'object' ? props.model.id : props.model)
                }
            });

            if (response.errors) {
                throw response.errors;
            }

            if (!response.data?.bookingEvent) {
                setComponentState('No data');
            } else {
                setModel({
                    id: response.data.bookingEvent.id,
                    name: response.data.bookingEvent.name,
                    note: response.data.bookingEvent.note || '',
                    bookingId: response.data.bookingEvent.bookingId,
                    date: response.data.bookingEvent.date ? new Date(response.data.bookingEvent.date) : null,
                    isDone: response.data.bookingEvent.isDone,
                    group: response.data.bookingEvent.group || null,
                    order: response.data.bookingEvent.order,
                    booking: response.data.bookingEvent.booking ? {
                        id: response.data.bookingEvent.booking.id,
                        inSeasonId: response.data.bookingEvent.booking.inSeasonId,
                        inDate: new Date(response.data.bookingEvent.booking.inDate),
                        outDate: new Date(response.data.bookingEvent.booking.outDate),
                        roomType: {
                            id: response.data.bookingEvent.booking.roomType.id,
                            name: response.data.bookingEvent.booking.roomType.name,
                            color: response.data.bookingEvent.booking.roomType.color || undefined
                        },
                        season: response.data.bookingEvent.booking.season
                    } : null,
                    insertType: response.data.bookingEvent.insertType || InsertTypes.User,
                    assigneeId: response.data.bookingEvent.assigneeId,
                    assignee: response.data.bookingEvent.assignee
                });
                setComponentState('');
            }
        } catch (error: any) {
            context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('error');
            throw error;
        }
    }
    // #endregion load data

    const getView = () => {
        if (!model) return [];

        const data: ItemViewPropsType['model'] = [
            {
                name: 'name',
                items: [
                    {
                        name: 'id',
                        label: 'fields.id.name',
                        value: model?.id,
                    },
                    {
                        name: 'bookingId',
                        label: 'fields.booking.name',
                        value: !model.booking ? <></> : <>
                            <div>
                                <span><BookingIdView id={model?.bookingId || model.booking.id} inSeasonId={model?.booking.inSeasonId || model?.bookingId || model.booking.id} /></span>
                                <HTMLCode />
                                <HTMLCode />
                                <HTMLCode />
                                <HTMLCode />
                                <AppLink to={AppRoutes.ACCOMMODATION_BOOKING_VIEW} params={{ id: model?.bookingId || model.booking.id }}>
                                    <FontAwesomeIcon icon={faUpRightFromSquare} />
                                </AppLink>
                                <br />
                                <div className="text-sm">
                                    <RoomTypeView {...model!.booking.roomType} />
                                    <br />
                                    <span>{DateUtil.format(model!.booking.inDate, DateFormats.SHORT)} - {DateUtil.format(model!.booking.outDate, DateFormats.SHORT)}, {model!.booking.season.name}</span>
                                </div>
                            </div>
                        </> ,
                    },
                    {
                        name: 'name',
                        label: 'fields.name.name',
                        value: <EventNameView item={model} />,
                    },
                    {
                        name: 'assignee',
                        label: 'fields.assignee.name',
                        value: model.assignee?.name,
                    },
                    {
                        name: 'date',
                        label: 'fields.date.name',
                        value: model?.date,
                        propertyType: 'date',
                    },
                    {
                        name: 'isDone',
                        label: 'fields.is-done.name',
                        value: <BooleanView square value={model?.isDone} />
                    },
                    {
                        name: 'note',
                        label: 'fields.note.name',
                        value: model?.note,
                        propertyType: 'note',
                    }
                ]
            }
        ];

        return data;
    }

    const onEdit = () => {
        context.modal.open(ADD_MODAL_ID, model?.id);
    };

    const onChangeIsDone = async () => {
        setComponentState('saving');
        try {
            const response = await changeEventIsDone({ id: model!.id!, isDone: !model!.isDone });

            if (response.success) {
                context.notify('success', 'request.save.success');
                setComponentState('');
                await update();
            }

        } catch (error: any) {
            context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('error');
            throw error;
        }
    };

    const update = async () => {
        context.modal.close(ADD_MODAL_ID);
        if (props.onUpdate) {
            props.onUpdate();
        }
        await loadData();
    }

    const onDelete = async () => {
        try {
            const request = await api.delete(`/BookingEvent/DeleteGroup?id=${model?.id}&mode=${deleteConfig.id}`);
            if (!request.success) {
                throw request.statusText;
            }

            context.notify('success', 'request.delete.success');
            if (props.onUpdate) {
                props.onUpdate();
            }
            setComponentState('');
            context.modal.close(DELETE_MODAL_HASH);
        }
        catch (error: any) {
            context.notify('error', 'request.delete.error', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('');
            throw error;
        }
    }

    return (
        <>
            <div className="w-full">
                <ComponentState state={componentState}>
                    <ItemView model={getView()} name={props.name} view="default" />
                    <If condition={hasWriteRole}>
                        <hr />
                        <div className="grid grid-cols-4 gap-5 w-full pt-5 text-right">
                            <Button disabled={componentState !== ''} title="button.reload" theme="link" onClick={loadData}>
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                            <Button disabled={componentState !== ''} title="button.edit" theme="link primary" onClick={onEdit}>
                                <FontAwesomeIcon icon={faPen} />
                            </Button>
                            <Button disabled={componentState !== ''} title={`accommodation.event.${model?.isDone ? 'markAsUndone' : 'markAsDone'}`} theme="link primary" onClick={onChangeIsDone}>
                                <FontAwesomeIcon icon={model?.isDone ? faXmarkSquare : faCheckSquare} />
                            </Button>
                            <Button disabled={componentState !== ''} title="button.delete" theme="link" onClick={() => context.modal.open(DELETE_MODAL_HASH)}>
                                <FontAwesomeIcon icon={faTrash} className="text-danger" />
                            </Button>
                        </div>
                    </If>
                </ComponentState>
            </div>
            <If condition={hasWriteRole}>
                {/* <DeleteConfirmationModal
                    name={DELETE_MODAL_HASH}
                    onConfirm={onDelete}
                    id={model?.id || 0}
                    onAfterConfirmationExecution={props.onUpdate}
                /> */}
                <ModalPortal name={DELETE_MODAL_HASH} title={context.t('common.confirmation')}>
                    <div className="py-5">
                        <div className="w-full text-center p-10">
                            <p>{t('confirmations.deleteItremConfirmation')}</p>
                        </div>
                        <If condition={model?.group}>
                            <div className="pb-10">
                                <DropdownInput
                                    label={"accommodation.event.delete.upcommingGroupEvents"}
                                    name="deleteGroupEvents"
                                    dataSource={{
                                        data: DELETE_GROUP_EVENT_MODE
                                    }}
                                    getItemId={i => i.id}
                                    view={{
                                        inputView: i => t(i!.name)
                                    }}
                                    value={deleteConfig}
                                    onChange={i => setDeleteConfig(i!)}
                                    required
                                />
                            </div>
                        </If>
                        <div className="grid grid-cols-2 w-max mx-auto gap-5">
                             <Button type="button" theme="primary" onClick={onDelete} title="yes">
                                <Translate i18nKey="common.yes" />
                            </Button>
                            <Button type="button" onClick={() => context.modal.close(DELETE_MODAL_HASH)} title="no">
                                <Translate i18nKey="common.no" />
                            </Button>
                        </div>
                    </div>
                </ModalPortal>
            </If>
            <ModalPortal name={ADD_MODAL_ID} title="pages.accommodation.event.form">
                {searchParams.has(ADD_MODAL_ID) && hasWriteRole ? (
                    <EventForm name="accommodationEventDatalist" bookingId={model!.bookingId || model!.booking?.id}
                        modelId={model!.id} onSave={update} />
                ) : <></>}
            </ModalPortal>
        </>
    );
};

export default EventView;
